import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import {
  ClipboardList,
  CheckSquare,
  Plus,
  Edit2,
  Trash2,
  FileCheck,
  Wrench,
  GripVertical,
  Save,
  X,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const phaseLabels = {
  COMERCIAL: "Fase Comercial",
  ADMINISTRATIVA: "Fase Administrativa",
  TECNICA: "Fase Técnica",
};

const phaseColors = {
  COMERCIAL: "bg-blue-500",
  ADMINISTRATIVA: "bg-yellow-500",
  TECNICA: "bg-orange-500",
};

const phaseIcons = {
  COMERCIAL: FileCheck,
  ADMINISTRATIVA: ClipboardList,
  TECNICA: Wrench,
};

const ChecklistMasterPage = () => {
  const { canDelete, canView } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePhase, setActivePhase] = useState("COMERCIAL");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    phase: "COMERCIAL",
    product_type: null,
    order: 0,
  });

  useEffect(() => {
    fetchTasks();
    fetchProductTypes();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/checklist-tasks`);
      setTasks(response.data);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Error al cargar tareas");
    } finally {
      setLoading(false);
    }
  };

  const fetchProductTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/product-types`);
      setProductTypes(response.data);
    } catch (error) {
      console.error("Error fetching product types:", error);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error("El nombre de la tarea es obligatorio");
      return;
    }

    if (formData.phase === "TECNICA" && !formData.product_type) {
      toast.error("El tipo de producto es obligatorio para tareas técnicas");
      return;
    }

    try {
      await axios.post(`${API_URL}/api/checklist-tasks`, formData);
      toast.success("Tarea creada correctamente");
      setCreateModalOpen(false);
      setFormData({ name: "", phase: "COMERCIAL", product_type: null, order: 0 });
      fetchTasks();
    } catch (error) {
      console.error("Error creating task:", error);
      toast.error(error.response?.data?.detail || "Error al crear tarea");
    }
  };

  const handleUpdate = async () => {
    if (!selectedTask || !formData.name.trim()) {
      toast.error("El nombre de la tarea es obligatorio");
      return;
    }

    if (formData.phase === "TECNICA" && !formData.product_type) {
      toast.error("El tipo de producto es obligatorio para tareas técnicas");
      return;
    }

    try {
      await axios.put(`${API_URL}/api/checklist-tasks/${selectedTask.id}`, formData);
      toast.success("Tarea actualizada correctamente");
      setEditModalOpen(false);
      setSelectedTask(null);
      fetchTasks();
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error(error.response?.data?.detail || "Error al actualizar tarea");
    }
  };

  const handleDelete = async () => {
    if (!selectedTask) return;

    try {
      await axios.delete(`${API_URL}/api/checklist-tasks/${selectedTask.id}`);
      toast.success("Tarea eliminada correctamente");
      setDeleteDialogOpen(false);
      setSelectedTask(null);
      fetchTasks();
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error(error.response?.data?.detail || "Error al eliminar tarea");
    }
  };

  const openEditModal = (task) => {
    setSelectedTask(task);
    setFormData({
      name: task.name,
      phase: task.phase,
      product_type: task.product_type,
      order: task.order,
    });
    setEditModalOpen(true);
  };

  const openDeleteDialog = (task) => {
    setSelectedTask(task);
    setDeleteDialogOpen(true);
  };

  const openCreateModal = () => {
    setFormData({
      name: "",
      phase: activePhase,
      product_type: activePhase === "TECNICA" ? productTypes[0] || null : null,
      order: tasks.filter(t => t.phase === activePhase).length,
    });
    setCreateModalOpen(true);
  };

  const filteredTasks = tasks.filter(t => t.phase === activePhase);

  // Group technical tasks by product type
  const groupedTechnicalTasks = activePhase === "TECNICA" 
    ? filteredTasks.reduce((acc, task) => {
        const type = task.product_type || "General";
        if (!acc[type]) acc[type] = [];
        acc[type].push(task);
        return acc;
      }, {})
    : {};

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#42E695]"></div>
      </div>
    );
  }

  // RBAC v2: page access is data-driven via the matrix (checklist_master.view).
  // SUPERADMIN grants it by default; admins may extend it to other roles.
  if (!canView("checklist_master")) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <ClipboardList className="h-16 w-16 text-slate-400 mx-auto mb-4" />
          <h2 className="text-xl text-white font-semibold">Acceso Restringido</h2>
          <p className="text-slate-400 mt-2">No tienes permiso para gestionar el checklist maestro</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="checklist-master-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-semibold text-white flex items-center gap-3">
            <CheckSquare className="h-8 w-8 text-[#03BEE4]" />
            Checklist Maestro
          </h1>
          <p className="text-slate-400 mt-1">
            Define las tareas de verificación para cada fase del proceso de provisión
          </p>
        </div>
        <Button
          onClick={openCreateModal}
          className="bg-[#42E695] text-[#04042C] hover:bg-[#34D399]"
          data-testid="create-task-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nueva Tarea
        </Button>
      </div>

      {/* Phase Tabs */}
      <Tabs value={activePhase} onValueChange={setActivePhase} className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-[#1B1B4B]">
          <TabsTrigger 
            value="COMERCIAL" 
            className="data-[state=active]:bg-blue-500 data-[state=active]:text-white"
          >
            <FileCheck className="h-4 w-4 mr-2" />
            Comercial ({tasks.filter(t => t.phase === "COMERCIAL").length})
          </TabsTrigger>
          <TabsTrigger 
            value="ADMINISTRATIVA"
            className="data-[state=active]:bg-yellow-500 data-[state=active]:text-white"
          >
            <ClipboardList className="h-4 w-4 mr-2" />
            Administrativa ({tasks.filter(t => t.phase === "ADMINISTRATIVA").length})
          </TabsTrigger>
          <TabsTrigger 
            value="TECNICA"
            className="data-[state=active]:bg-orange-500 data-[state=active]:text-white"
          >
            <Wrench className="h-4 w-4 mr-2" />
            Técnica ({tasks.filter(t => t.phase === "TECNICA").length})
          </TabsTrigger>
        </TabsList>

        {/* Commercial and Administrative Tasks (same structure) */}
        {["COMERCIAL", "ADMINISTRATIVA"].map(phase => (
          <TabsContent key={phase} value={phase} className="mt-4">
            <Card className="bg-[#0A0A35] border-[#1E1E5F]">
              <CardHeader>
                <CardTitle className={`${phase === "COMERCIAL" ? "text-blue-400" : "text-yellow-400"} flex items-center gap-2`}>
                  {phase === "COMERCIAL" ? <FileCheck className="h-5 w-5" /> : <ClipboardList className="h-5 w-5" />}
                  Tareas de {phaseLabels[phase]}
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Estas tareas aplican a todas las provisiones en la fase {phase.toLowerCase()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {filteredTasks.length === 0 ? (
                  <div className="text-center py-8">
                    <ClipboardList className="h-12 w-12 text-slate-500 mx-auto mb-3" />
                    <p className="text-slate-400">No hay tareas definidas para esta fase</p>
                    <Button
                      onClick={openCreateModal}
                      variant="outline"
                      className="mt-4 border-[#1E1E5F] text-slate-300"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Crear Primera Tarea
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#1E1E5F]">
                        <TableHead className="text-slate-400 w-12">#</TableHead>
                        <TableHead className="text-slate-400">Tarea</TableHead>
                        <TableHead className="text-slate-400 text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTasks.sort((a, b) => a.order - b.order).map((task, index) => (
                        <TableRow key={task.id} className="border-[#1E1E5F] hover:bg-[#1B1B4B]/50">
                          <TableCell className="text-slate-400 font-mono">
                            {index + 1}
                          </TableCell>
                          <TableCell className="text-white">
                            {task.name}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditModal(task)}
                                className="text-[#03BEE4] hover:text-[#03BEE4]/80"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              {canDelete("checklist_master") && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openDeleteDialog(task)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}

        {/* Technical Tasks (grouped by product type) */}
        <TabsContent value="TECNICA" className="mt-4 space-y-4">
          <Card className="bg-[#0A0A35] border-[#1E1E5F]">
            <CardHeader>
              <CardTitle className="text-orange-400 flex items-center gap-2">
                <Wrench className="h-5 w-5" />
                Tareas de Fase Técnica
              </CardTitle>
              <CardDescription className="text-slate-400">
                Las tareas técnicas se pueden personalizar por tipo de producto. Cada ticket técnico recibirá las tareas correspondientes a su tipo.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {Object.keys(groupedTechnicalTasks).length === 0 ? (
                <div className="text-center py-8">
                  <Wrench className="h-12 w-12 text-slate-500 mx-auto mb-3" />
                  <p className="text-slate-400">No hay tareas técnicas definidas</p>
                  <Button
                    onClick={openCreateModal}
                    variant="outline"
                    className="mt-4 border-[#1E1E5F] text-slate-300"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Crear Primera Tarea
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(groupedTechnicalTasks).map(([productType, typeTasks]) => (
                    <div key={productType} className="border border-[#1E1E5F] rounded-lg overflow-hidden">
                      <div className="bg-[#1B1B4B] px-4 py-2 flex items-center justify-between">
                        <h3 className="text-white font-medium flex items-center gap-2">
                          <Badge className="bg-orange-500 text-white">{productType}</Badge>
                          <span className="text-slate-400 text-sm">({typeTasks.length} tareas)</span>
                        </h3>
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow className="border-[#1E1E5F]">
                            <TableHead className="text-slate-400 w-12">#</TableHead>
                            <TableHead className="text-slate-400">Tarea</TableHead>
                            <TableHead className="text-slate-400 text-right">Acciones</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {typeTasks.sort((a, b) => a.order - b.order).map((task, index) => (
                            <TableRow key={task.id} className="border-[#1E1E5F] hover:bg-[#1B1B4B]/50">
                              <TableCell className="text-slate-400 font-mono">
                                {index + 1}
                              </TableCell>
                              <TableCell className="text-white">
                                {task.name}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openEditModal(task)}
                                    className="text-[#03BEE4] hover:text-[#03BEE4]/80"
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </Button>
                                  {canDelete("checklist_master") && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openDeleteDialog(task)}
                                    className="text-red-400 hover:text-red-300"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Task Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="bg-[#0A0A35] border-[#1E1E5F] text-white">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Plus className="h-5 w-5 text-[#42E695]" />
              Nueva Tarea de Checklist
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">Nombre de la Tarea *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Verificar documentación completa"
                className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                data-testid="task-name-input"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Fase</Label>
              <Select
                value={formData.phase}
                onValueChange={(value) => setFormData({ 
                  ...formData, 
                  phase: value,
                  product_type: value === "TECNICA" ? productTypes[0] || null : null
                })}
              >
                <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                  <SelectItem value="COMERCIAL" className="text-white">
                    <div className="flex items-center gap-2">
                      <FileCheck className="h-4 w-4 text-blue-400" />
                      Fase Comercial
                    </div>
                  </SelectItem>
                  <SelectItem value="ADMINISTRATIVA" className="text-white">
                    <div className="flex items-center gap-2">
                      <ClipboardList className="h-4 w-4 text-yellow-400" />
                      Fase Administrativa
                    </div>
                  </SelectItem>
                  <SelectItem value="TECNICA" className="text-white">
                    <div className="flex items-center gap-2">
                      <Wrench className="h-4 w-4 text-orange-400" />
                      Fase Técnica
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.phase === "TECNICA" && (
              <div>
                <Label className="text-slate-300">Tipo de Producto *</Label>
                <Select
                  value={formData.product_type || ""}
                  onValueChange={(value) => setFormData({ ...formData, product_type: value })}
                >
                  <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                    <SelectValue placeholder="Seleccionar tipo de producto" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                    {productTypes.map(type => (
                      <SelectItem key={type} value={type} className="text-white">
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-1">
                  Esta tarea aparecerá en los tickets técnicos de este tipo de producto
                </p>
              </div>
            )}

            <div>
              <Label className="text-slate-300">Orden</Label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white w-24"
              />
              <p className="text-xs text-slate-400 mt-1">
                Las tareas se ordenan de menor a mayor
              </p>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setCreateModalOpen(false)}
              className="border-[#1E1E5F] text-slate-300"
            >
              <X className="h-4 w-4 mr-2" />
              Cancelar
            </Button>
            <Button
              onClick={handleCreate}
              className="bg-[#42E695] text-[#04042C]"
              data-testid="save-task-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              Crear Tarea
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Task Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-[#0A0A35] border-[#1E1E5F] text-white">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-[#03BEE4]" />
              Editar Tarea
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">Nombre de la Tarea *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Fase</Label>
              <Select
                value={formData.phase}
                onValueChange={(value) => setFormData({ 
                  ...formData, 
                  phase: value,
                  product_type: value === "TECNICA" ? formData.product_type : null
                })}
              >
                <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                  <SelectItem value="COMERCIAL" className="text-white">Fase Comercial</SelectItem>
                  <SelectItem value="ADMINISTRATIVA" className="text-white">Fase Administrativa</SelectItem>
                  <SelectItem value="TECNICA" className="text-white">Fase Técnica</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.phase === "TECNICA" && (
              <div>
                <Label className="text-slate-300">Tipo de Producto *</Label>
                <Select
                  value={formData.product_type || ""}
                  onValueChange={(value) => setFormData({ ...formData, product_type: value })}
                >
                  <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                    <SelectValue placeholder="Seleccionar tipo de producto" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                    {productTypes.map(type => (
                      <SelectItem key={type} value={type} className="text-white">{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-slate-300">Orden</Label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white w-24"
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setEditModalOpen(false)}
              className="border-[#1E1E5F] text-slate-300"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleUpdate}
              className="bg-[#42E695] text-[#04042C]"
            >
              <Save className="h-4 w-4 mr-2" />
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#0A0A35] border-[#1E1E5F]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-400" />
              Eliminar Tarea
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              ¿Estás seguro de eliminar la tarea <span className="text-white font-semibold">"{selectedTask?.name}"</span>?
              Esta acción no afectará las provisiones existentes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#1E1E5F] text-slate-300 hover:bg-[#1B1B4B]">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ChecklistMasterPage;
